import type { TableFilterState } from '@ibsys/kit';
import { isRecord } from '@/shared/lib';
import {
  CONVERSATION_IDS_FILTER_ID,
  CONVERSATION_SELECTION_SOURCES,
  type ConversationSelection,
  type ConversationSelectionSource,
} from '../model/selection';

/**
 * Распознаёт внешнюю выборку диалогов в состоянии навигации.
 * В состоянии роутера страницы диалогов лежат и другие значения (например, строка
 * поисковых параметров списка), поэтому форма проверяется целиком.
 */
export const isConversationSelection = (state: unknown): state is ConversationSelection => {
  if (!isRecord(state)) return false;
  const { conversationIds, description, source } = state as Partial<ConversationSelection>;
  return (
    Array.isArray(conversationIds) &&
    conversationIds.every((id) => typeof id === 'number') &&
    typeof description === 'string' &&
    typeof source === 'string' &&
    (CONVERSATION_SELECTION_SOURCES as readonly string[]).includes(source)
  );
};

/**
 * Приводит сохранённый источник выборки к известному значению.
 * Неизвестное или отсутствующее (в т.ч. из настроек, сохранённых до появления
 * зон качества) трактуется как авто-инсайт — прежнее поведение.
 */
export const normalizeConversationSelectionSource = (value: string | undefined): ConversationSelectionSource =>
  (CONVERSATION_SELECTION_SOURCES as readonly string[]).includes(value ?? '')
    ? (value as ConversationSelectionSource)
    : 'autoInsight';

/**
 * Ключ состава выборки, не зависящий от порядка идентификаторов. Так выборку опознавали, пока её не
 * стали различать описанием: пресет и скрытое предложение сохранить, оставшиеся с той поры, несут
 * только состав, и опознаются они им же.
 */
export const buildConversationSelectionIdsKey = (conversationIds: number[]): string =>
  [...conversationIds].sort((a, b) => a - b).join(',');

/**
 * Ключ выборки — её состав, не зависящий от порядка идентификаторов, вместе с описанием.
 * Один состав у разных элементов дашборда совпадает часто: средний балл любого критерия считается
 * по всем диалогам, где он оценён. Такие выборки различает описание — то, откуда они открыты.
 */
export const buildConversationSelectionKey = ({
  conversationIds,
  description,
}: Pick<ConversationSelection, 'conversationIds' | 'description'>): string =>
  `${buildConversationSelectionIdsKey(conversationIds)}:${description}`;

/**
 * Возвращает состав выборки, лежащей среди фильтров таблицы.
 * `null` — фильтра выборки в состоянии таблицы нет.
 */
export const getConversationSelectionIds = (filters: TableFilterState): number[] | null => {
  const value = filters.find((filter) => filter.id === CONVERSATION_IDS_FILTER_ID)?.value;
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.filter((id): id is number => typeof id === 'number');
};
